import { HistoryItem, IndicatorStatusEnum } from '@api/indicatorsApiHandler';
import leftArrow from '@assets/images/leftArow.svg';
import Img from '@components/Atoms/Img';
import { WingDetails } from '@components/Star';
import { useAppDispatch } from '@hooks/useAppDispatch';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { generateStar } from '@utils/star.util';
import dayjs from 'dayjs';
import EChartsReact from 'echarts-for-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  setChartSelectedIndicatorDetails,
  setLoadingScores,
} from 'store/Slices/AssessmentScoresSliceV2';
import styles from './index.module.scss';

const generalHappiness = '';

interface AreaProps {
  scores: HistoryItem[];
  stars?: WingDetails[][];
  onScoreClick?: (scoreData: {
    id: number;
    score: number;
    date: string;
    index: number;
    indicatorId: number;
    scoreSourceText: string;
    indicatorName: string;
    status: IndicatorStatusEnum;
    isPositive: boolean;
  }) => void;
}

interface RichDataObject {
  [key: string]: string | number | object;
}

export const IndicatorAreaChart: React.FunctionComponent<AreaProps> = (props) => {
  const { scores, stars = [], onScoreClick } = props;
  const { isDesktop } = useWindowDimensions();
  const dispatch = useAppDispatch();
  const ITEMS_PER_PAGE = isDesktop ? 10 : 4;
  const chartRef = useRef() as any;

  const sortedScores = useMemo(
    () =>
      [...scores].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [scores],
  );

  const totalPages = Math.ceil(sortedScores.length / ITEMS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;

  const prevPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [currentPage, totalPages]);

  const nextPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [currentPage]);

  const paginatedScores = useMemo(() => {
    const pageData = sortedScores.slice(startIndex, endIndex);

    return pageData.reverse();
  }, [sortedScores, startIndex, endIndex]);

  const modifiedValues = paginatedScores.map((score, index) => {
    const originalIndexV = sortedScores.findIndex((item) => item.id === score.id);

    return {
      score: score.score,
      id: score.id,
      date: dayjs(score.createdAt).format('DD/MM/YYYY'),
      updated: score.createdAt,
      originalIndex: originalIndexV,
      indicatorId: score?.indicatorId,
      scoreSourceText: score?.scoreSourceText,
      indicatorName: score?.indicator?.name,
      status: score?.status,
      isPositive: score?.indicator?.isPositive || true,
    };
  });

  const modifiedLabels = paginatedScores.map((score) => dayjs(score.createdAt).format('DD MMM YY'));

  if (paginatedScores.length === 1) {
    modifiedValues.unshift({
      ...modifiedValues[0],
      originalIndex: -1,
    });
    modifiedLabels.unshift('');
  }

  const maxValue = isDesktop ? 10 : 4;

  let modifiedStars = [...stars];

  if (stars.length < maxValue) {
    const emptyStars = new Array(maxValue - stars.length).fill([]);
    modifiedStars = [...emptyStars, ...stars];
  }

  modifiedStars.push([]);

  const data = modifiedValues.map((value, index) => ({
    score: Math.round(value.score * 100) / 100,
    id: value.id,
    date: value.date,
    index: value.originalIndex,
    localIndex: index,
    indicatorId: value?.indicatorId,
    scoreSourceText: value?.scoreSourceText,
    indicatorName: value?.indicatorName,
    status: value?.status,
    isPositive: value?.isPositive,
  }));

  const labelData = modifiedLabels;

  const series = data.map((value, index) => {
    if (labelData[index] === '') {
      return {
        value: value.score,
        id: value.id,
        index: value.index,
        localIndex: value.localIndex,
        date: value.date,
        label: { show: false },
        symbol: 'none',
        symbolSize: 0,
        indicatorId: value?.indicatorId,
        scoreSourceText: value?.scoreSourceText,
        indicatorName: value?.indicatorName,
        status: value?.status,
        isPositive: value?.isPositive,
      };
    }

    if (value.index === selectedIndex) {
      return {
        value: value.score,
        id: value.id,
        index: value.index,
        localIndex: value.localIndex,
        date: value.date,
        symbolSize: 67,
        label: {
          fontSize: 20,
        },
        indicatorId: value?.indicatorId,
        scoreSourceText: value?.scoreSourceText,
        indicatorName: value?.indicatorName,
        status: value?.status,
        isPositive: value?.isPositive,
      };
    }

    return {
      value: value.score,
      id: value.id,
      index: value.index,
      localIndex: value.localIndex,
      date: value.date,
      label: { show: true },
      indicatorId: value?.indicatorId,
      scoreSourceText: value?.scoreSourceText,
      indicatorName: value?.indicatorName,
      status: value?.status,
      isPositive: value?.isPositive,
    };
  });

  const richData: RichDataObject = {
    value: {
      fontFamily: 'Titillium Web',
      fontSize: 14,
      fontWeight: 400,
      lineHeight: 30,
      align: 'center',
    },
    selected: {
      height: 4,
      backgroundColor: '#FCD300',
      width: 60,
      lineHeight: 15,
    },
  };

  const handleDataPointClick = useCallback(
    (
      id: number,
      date: string,
      index: number,
      score: number,
      indicatorId: number,
      scoreSourceText: string,
      indicatorName: string | any,
      status: IndicatorStatusEnum | any,
      isPositive: boolean,
    ) => {
      setSelectedIndex(index);
      requestAnimationFrame(() => {
        dispatch(setLoadingScores(false));
      });

      const indicatorDetails = {
        id,
        score,
        date,
        index,
        indicatorId,
        scoreSourceText,
        indicatorName,
        status,
        isPositive,
      };

      dispatch(setChartSelectedIndicatorDetails(indicatorDetails));

      if (onScoreClick) {
        onScoreClick({
          id,
          score,
          date,
          index,
          indicatorId,
          scoreSourceText,
          indicatorName,
          status,
          isPositive,
        });
      }
    },
    [onScoreClick],
  );

  labelData.forEach((item) => {
    if (item !== '') {
      richData[8] = {
        height: 0,
        backgroundColor: {
          image: generateStar(372, 428, [
            {
              color: '#019DF2',
              name: generalHappiness,
              value: [10, 10, 6, 6],
            },
            {
              color: '#BEAAF3',
              name: generalHappiness,
              value: [10, 10, 6, 6],
            },
            {
              color: '#FCD300',
              name: generalHappiness,
              value: [10, 10, 6, 6],
            },
            {
              color: '#8EEA36',
              name: generalHappiness,
              value: [10, 10, 6, 6],
            },
            {
              color: '#06BF97',
              name: generalHappiness,
              value: [10, 10, 6, 6],
            },
            {
              color: '#FF8A4A',
              name: generalHappiness,
              value: [10, 10, 6, 6],
            },
          ]),
        },
      };
    }
  });

  useEffect(() => {
    const instance = chartRef?.current?.getEchartsInstance();

    if (instance) {
      instance.on('click', (params: any) => {
        const clickedItem = series[params?.dataIndex];

        if (clickedItem) {
          handleDataPointClick(
            clickedItem.id,
            clickedItem.date,
            clickedItem.index,
            clickedItem?.value || 0,
            clickedItem?.indicatorId,
            clickedItem?.scoreSourceText,
            clickedItem?.indicatorName || '',
            clickedItem?.status,
            clickedItem?.isPositive,
          );
        }
      });
    }
  }, [series, handleDataPointClick]);

  const option = {
    grid: { right: 40, left: 40 },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      triggerEvent: true,
      data: labelData,
      axisPointer: {
        show: true,
        type: 'shadow',
        triggerEvent: false,
        trigger: 'item',
      },
      axisLabel: {
        margin: 30,
        align: 'center',
        rotate: 20,
        triggerEvent: true,
        trigger: 'axis',
        interval: 0,
        formatter: (value: string, index: number) => {
          const isCurrentItem = data[index]?.index === selectedIndex;
          const isLatestItem = data[index]?.index === 0;

          return [
            `{${value !== '' ? 8 : 0}|}`,
            `${isLatestItem ? 'Current' : value}`,
            isCurrentItem ? `{selected|}` : '',
          ].join('\n');
        },
        fontWeight: 'bold',
        rich: richData,
      },
    },
    yAxis: {
      type: 'value',
      interval: 10,
      max: 100,
      min: 0,
      triggerEvent: true,
      trigger: 'axis',
      axisLine: {
        show: true,
      },
      splitLine: {
        show: true,
      },
    },
    series: [
      {
        data: series,
        type: 'line',
        symbolSize: 44,
        smooth: true,
        label: {
          show: true,
          position: 'inside',
          triggerEvent: true,
          fontSize: 16,
          fontFamily: 'Titillium Web',
          color: '#505050',
        },
        itemStyle: {
          color: '#069B7B',
        },
        lineStyle: {
          color: '#06BF97',
        },
        areaStyle: {
          color: '#06BF97',
          opacity: 0.9,
        },
      },
    ],
  };

  return (
    <div className={styles.hsAreaChart} style={{ maxWidth: '100%' }}>
      {/* Left arrow - shows older data - only show when there are previous pages available */}
      {currentPage < totalPages && (
        <div onClick={prevPage} style={{ cursor: 'pointer' }}>
          <Img src={leftArrow} alt="Previous Data" className={styles.arrowContLeft} />
        </div>
      )}
      <EChartsReact ref={chartRef} option={option} className={styles.eChart} />
      {/* Right arrow - shows newer data (back to latest) - only show when NOT on the latest page */}
      {currentPage > 1 && (
        <div onClick={nextPage} style={{ cursor: 'pointer' }}>
          <Img src={leftArrow} alt="Back to Latest" className={styles.arrowContRight} />
        </div>
      )}
    </div>
  );
};

IndicatorAreaChart.defaultProps = {
  stars: [],
  onScoreClick: undefined,
};
